/**
 * Clipboard / attach helpers for the chat composer.
 * Windows screen grabs often yield empty File.type; Explorer "Copy" pastes files via items/files.
 * Allowlist aligns with api/apps/threads/services/attachment_policy.py (common types).
 */

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.heic'] as const;

/** `<input type="file" accept>` — keep in sync with `isComposerAttachableFile`. */
export const COMPOSER_FILE_ACCEPT = [
  'image/*',
  '.pdf',
  '.txt',
  '.md',
  '.csv',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.rtf',
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/pdf',
  'application/msword',
  'application/rtf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');

const MIME_BY_EXTENSION: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.heic': 'image/heic',
  '.pdf': 'application/pdf',
  '.txt': 'text/plain',
  '.md': 'text/markdown',
  '.csv': 'text/csv',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.rtf': 'application/rtf',
};

function extensionForImageMime(mime: string): string {
  const m = (mime || '').toLowerCase().split(';')[0].trim();
  if (m === 'image/jpeg') return 'jpg';
  if (m === 'image/webp') return 'webp';
  if (m === 'image/gif') return 'gif';
  if (m === 'image/heic') return 'heic';
  return 'png';
}

function timestampForFilename(): string {
  return new Date().toISOString().replace(/\D/g, '').slice(0, 14);
}

function fileExtension(name: string): string {
  const lower = (name || '').toLowerCase();
  const dot = lower.lastIndexOf('.');
  if (dot < 0) return '';
  return lower.slice(dot);
}

function hasImageExtension(name: string): boolean {
  return IMAGE_EXTENSIONS.some((ext) => (name || '').toLowerCase().endsWith(ext));
}

function mimeFromExtension(name: string): string | undefined {
  return MIME_BY_EXTENSION[fileExtension(name)];
}

/** True when MIME says image, or filename has a common image extension. */
export function isImageLikeFile(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('image/')) return true;
  return hasImageExtension(file.name || '');
}

/**
 * Files the composer may attach (paste / drop / picker).
 * Matches thread attachment policy for common document types.
 */
export function isComposerAttachableFile(file: File): boolean {
  const type = (file.type || '').toLowerCase().split(';')[0].trim();
  const name = (file.name || '').toLowerCase();

  if (type.startsWith('image/') || hasImageExtension(name)) return true;
  if (type === 'application/pdf' || name.endsWith('.pdf')) return true;
  if (
    type === 'text/plain' ||
    type === 'text/markdown' ||
    type === 'text/csv' ||
    type === 'application/csv' ||
    name.endsWith('.txt') ||
    name.endsWith('.md') ||
    name.endsWith('.csv')
  ) {
    return true;
  }
  if (type === 'application/msword' || name.endsWith('.doc')) return true;
  if (
    type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    name.endsWith('.docx')
  ) {
    return true;
  }
  if (type === 'application/vnd.ms-excel' || name.endsWith('.xls')) return true;
  if (
    type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    name.endsWith('.xlsx')
  ) {
    return true;
  }
  if (type === 'application/rtf' || name.endsWith('.rtf')) return true;
  if (type.startsWith('application/vnd.openxmlformats-officedocument')) return true;
  return false;
}

/**
 * Always normalize MIME (and name when missing). Clipboard items may report
 * `image/png` while `File.type` is empty — especially Windows screen grabs.
 */
export function normalizeClipboardImageFile(
  file: File,
  index: number,
  mimeHint?: string
): File {
  const resolvedType = (file.type || mimeHint || 'image/png').split(';')[0].trim() || 'image/png';
  const needsRename = !file.name;
  const needsTypeFix = !file.type || file.type !== resolvedType;

  if (!needsRename && !needsTypeFix) return file;

  const ext = extensionForImageMime(resolvedType);
  const suffix = index > 0 ? `-${index + 1}` : '';
  const name = needsRename
    ? `pasted-image-${timestampForFilename()}${suffix}.${ext}`
    : file.name;

  return new File([file], name, {
    type: resolvedType,
    lastModified: file.lastModified || Date.now(),
  });
}

/** Fill empty/mismatched MIME from clipboard item hint or filename extension. */
export function normalizeClipboardFile(file: File, index: number, mimeHint?: string): File {
  const hint = (mimeHint || '').split(';')[0].trim();
  const fromName = mimeFromExtension(file.name);
  const resolvedType =
    (file.type || hint || fromName || '').split(';')[0].trim() || 'application/octet-stream';

  if (resolvedType.startsWith('image/') || (!file.type && hint.startsWith('image/'))) {
    return normalizeClipboardImageFile(file, index, hint || resolvedType);
  }

  if (file.type === resolvedType && file.name) return file;

  const name =
    file.name ||
    `pasted-file-${timestampForFilename()}${index > 0 ? `-${index + 1}` : ''}${fileExtension(file.name) || ''}`;

  return new File([file], name, {
    type: resolvedType,
    lastModified: file.lastModified || Date.now(),
  });
}

type ClipboardItemLike = {
  kind: string;
  type: string;
  getAsFile: () => File | null;
};

type ClipboardDataLike = {
  items?: ArrayLike<ClipboardItemLike>;
  files?: ArrayLike<File> | null;
};

function pushUniqueFile(out: File[], seen: Set<string>, file: File): void {
  const key = `${file.name}|${file.size}|${file.type}|${file.lastModified}`;
  if (seen.has(key)) return;
  seen.add(key);
  out.push(file);
}

/**
 * Collect attachable files from paste clipboardData (items + files), normalizing MIME.
 * Prefer this over image-only collection for composer paste.
 */
export function collectClipboardAttachableFiles(
  clipboardData: ClipboardDataLike | null | undefined
): File[] {
  if (!clipboardData) return [];

  const files: File[] = [];
  const seen = new Set<string>();

  if (clipboardData.items) {
    for (const item of Array.from(clipboardData.items)) {
      if (item.kind !== 'file') continue;
      const raw = item.getAsFile();
      if (!raw) continue;
      const normalized = normalizeClipboardFile(raw, files.length, item.type);
      if (!isComposerAttachableFile(normalized)) continue;
      pushUniqueFile(files, seen, normalized);
    }
  }

  if (clipboardData.files?.length) {
    for (const raw of Array.from(clipboardData.files)) {
      const normalized = normalizeClipboardFile(raw, files.length);
      if (!isComposerAttachableFile(normalized)) continue;
      pushUniqueFile(files, seen, normalized);
    }
  }

  return files;
}

/** @deprecated Prefer collectClipboardAttachableFiles — kept for image-only call sites/tests. */
export function collectClipboardImageFiles(
  clipboardData: ClipboardDataLike | null | undefined
): File[] {
  return collectClipboardAttachableFiles(clipboardData).filter(isImageLikeFile);
}

/** Build a FileList for attach handlers (DataTransfer). */
export function fileListFromFiles(files: File[]): FileList | null {
  if (!files.length) return null;
  const transfer = new DataTransfer();
  for (const file of files) {
    transfer.items.add(file);
  }
  return transfer.files;
}

export function isEditableElement(el: Element | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return Boolean(el.closest('[contenteditable="true"], [contenteditable=""]'));
}
